import {
  type AcceptGeneratedCasesInput,
  type BugDetailView,
  type BugView,
  type CreateFixTaskInput,
  type CreateTestCaseInput,
  type CreateTestEnvironmentInput,
  type CreateTestSuiteInput,
  type ImportTestCasesInput,
  type OpenTestRoundInput,
  type AssignBugInput,
  type BugReasonInput,
  type MarkBugFixedInput,
  type VerifyBugInput,
  type RecordTestExecutionInput,
  type ReportBugInput,
  type TestingOverview,
  type TestingReport,
  type UpdateBugInput,
  type UpdateTestCaseInput,
  type UpdateTestEnvironmentInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Tab Kiểm thử — docs/plan-testing-tab.md.
 *
 * ## Vì sao gần như mọi lệnh trả về cả `TestingOverview`
 *
 * Một lượt ghi kết quả đổi nhiều thứ cùng lúc: ô trong lưới, trạng thái lỗi,
 * và (với vòng) cả danh sách vòng. Trả về cả bức tranh thì giao diện đặt lại
 * một lần và không bao giờ hiện ra một trạng thái nửa vời — rẻ hơn hẳn việc
 * tự suy ra phần nào vừa đổi, và không lệch được với server.
 */
const base = (projectId: string) => `/projects/${projectId}/testing`;

export const testingApi = {
  overview: (projectId: string, roundId?: string) =>
    apiFetch<TestingOverview>(
      roundId ? `${base(projectId)}?roundId=${encodeURIComponent(roundId)}` : base(projectId),
    ),

  bug: (projectId: string, bugId: string) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}`),

  /** Báo cáo §4. `asOf` **cắt** danh sách vòng — luật #7. */
  report: (projectId: string, asOf?: string) =>
    apiFetch<TestingReport>(
      asOf
        ? `${base(projectId)}/report?asOf=${encodeURIComponent(asOf)}`
        : `${base(projectId)}/report`,
    ),

  // ── Bộ case — `test:manage` ───────────────────────────────────────────────

  createSuite: (projectId: string, input: CreateTestSuiteInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/suites`, { method: 'POST', body: input }),

  createCase: (projectId: string, input: CreateTestCaseInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/cases`, { method: 'POST', body: input }),

  /**
   * Duyệt những case AI vừa đề xuất — APM-TEST-ai.
   *
   * `indexes` là vị trí trong `items` của gợi ý, không phải id: case chưa tồn
   * tại nên chưa có id nào để gọi tên. Bỏ tích một dòng = bỏ chỉ số đó ra.
   */
  acceptGeneratedCases: (
    projectId: string,
    suggestionId: string,
    input: AcceptGeneratedCasesInput,
  ) =>
    apiFetch<TestingOverview>(`${base(projectId)}/generated-cases/${suggestionId}/accept`, {
      method: 'POST',
      body: input,
    }),

  updateCase: (projectId: string, caseId: string, input: UpdateTestCaseInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/cases/${caseId}`, {
      method: 'POST',
      body: input,
    }),

  /** Nhân bản một case — thứ giữ cho PM khỏi gõ 60 case từ màn hình trắng. */
  duplicateCase: (projectId: string, caseId: string, newCode: string) =>
    apiFetch<TestingOverview>(`${base(projectId)}/cases/${caseId}/duplicate`, {
      method: 'POST',
      body: { newCode },
    }),

  /** Nhập case từ file — dòng đã chuẩn hoá và đã xem trước ở trình duyệt (APM-TIMP). */
  importCases: (projectId: string, input: ImportTestCasesInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/case-imports`, { method: 'POST', body: input }),

  createEnvironment: (projectId: string, input: CreateTestEnvironmentInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/environments`, {
      method: 'POST',
      body: input,
    }),

  /** Đổi tên hoặc bật/tắt — môi trường tắt không còn là cột trong lưới. */
  updateEnvironment: (
    projectId: string,
    environmentId: string,
    input: UpdateTestEnvironmentInput,
  ) =>
    apiFetch<TestingOverview>(`${base(projectId)}/environments/${environmentId}`, {
      method: 'POST',
      body: input,
    }),

  // ── Vòng test — `test:manage` ─────────────────────────────────────────────

  openRound: (projectId: string, input: OpenTestRoundInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/rounds`, { method: 'POST', body: input }),

  closeRound: (projectId: string, roundId: string) =>
    apiFetch<TestingOverview>(`${base(projectId)}/rounds/${roundId}/close`, { method: 'POST' }),

  reopenRound: (projectId: string, roundId: string) =>
    apiFetch<TestingOverview>(`${base(projectId)}/rounds/${roundId}/reopen`, { method: 'POST' }),

  deleteRound: (projectId: string, roundId: string) =>
    apiFetch<TestingOverview>(`${base(projectId)}/rounds/${roundId}`, { method: 'DELETE' }),

  // ── Chạy test — `test:execute` ────────────────────────────────────────────

  recordExecution: (projectId: string, roundId: string, input: RecordTestExecutionInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/rounds/${roundId}/executions`, {
      method: 'POST',
      body: input,
    }),

  // ── Lỗi ───────────────────────────────────────────────────────────────────

  /** Ghi lỗi mới chỉ cần `test:read` — xem docblock của controller. */
  reportBug: (projectId: string, input: ReportBugInput) =>
    apiFetch<TestingOverview>(`${base(projectId)}/bugs`, { method: 'POST', body: input }),

  updateBug: (projectId: string, bugId: string, input: UpdateBugInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}`, { method: 'POST', body: input }),

  // ── Luồng lỗi v2 — APM-TEST2-c, plan-testing-v2 §2 ─────────────────────
  //
  // Mỗi đường chuyển một route, luật quyền nằm ở lệnh phía server. Giao diện
  // chỉ **ẩn** nút người xem không bấm được — xem `bug-flow.ts`.

  /** Giao / tự nhận / trả lại (`assigneeId: null`). */
  assignBug: (projectId: string, bugId: string, input: AssignBugInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/assign`, {
      method: 'POST',
      body: input,
    }),

  /** "Đã sửa xong" — người được giao hoặc PM; không cần vòng mở. */
  markBugFixed: (projectId: string, bugId: string, input: MarkBugFixedInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/fixed`, {
      method: 'POST',
      body: input,
    }),

  /** ⚠️ "✓ Hết lỗi" / "✗ Vẫn lỗi" — đường duy nhất đóng được một lỗi. */
  verifyBug: (projectId: string, bugId: string, input: VerifyBugInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/verify`, {
      method: 'POST',
      body: input,
    }),

  /** "Không phải lỗi" — PM, lý do bắt buộc. */
  rejectBug: (projectId: string, bugId: string, input: BugReasonInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/reject`, {
      method: 'POST',
      body: input,
    }),

  /** Mở lại lỗi đã đóng / đã bác. */
  reopenBug: (projectId: string, bugId: string, input: BugReasonInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/reopen`, {
      method: 'POST',
      body: input,
    }),

  // ── Lỗi ↔ việc sửa lỗi (§9.2) ─────────────────────────────────────────────

  /** `task:create` — route này tạo một task thật, thứ sẽ vào kế hoạch dự án. */
  createFixTask: (projectId: string, bugId: string, input: CreateFixTaskInput) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/fix-task`, {
      method: 'POST',
      body: input,
    }),

  unlinkTask: (projectId: string, bugId: string, taskId: string) =>
    apiFetch<BugDetailView>(`${base(projectId)}/bugs/${bugId}/tasks/${taskId}`, {
      method: 'DELETE',
    }),

  /** Lỗi mà một việc đang sửa — cho chi tiết công việc. */
  bugsForTask: (projectId: string, taskId: string) =>
    apiFetch<BugView[]>(`${base(projectId)}/tasks/${taskId}/bugs`),
};

export const testingKeys = {
  overview: (projectId: string, roundId?: string) =>
    ['testing', 'overview', projectId, roundId ?? 'current'] as const,
  bug: (projectId: string, bugId: string) => ['testing', 'bug', projectId, bugId] as const,
  report: (projectId: string, asOf?: string) =>
    ['testing', 'report', projectId, asOf ?? 'now'] as const,
  bugsForTask: (projectId: string, taskId: string) =>
    ['testing', 'task-bugs', projectId, taskId] as const,
};
